// src/xml8.ts
import { Rectangle, Texture, AnimatedSprite } from "pixi.js";
import { Direction, DirectionArray } from "types/battle-engine/battle";

export type AnimSpec = {
  name: string;
  index: number; // which image to load, if you use name->file you can ignore
  frameWidth: number;
  frameHeight: number;
  durations: number[]; // per-frame ticks (we'll convert to ms)
  hitFrame?: number | undefined;
  rushFrame?: number | undefined;
  returnFrame?: number | undefined;
};

// Parse your AnimData.xml format
export async function loadAnimXML(
  url: string,
): Promise<Record<string, AnimSpec>> {
  const text = await (await fetch(url)).text();
  const doc = new DOMParser().parseFromString(text, "application/xml");

  const anims: Record<string, AnimSpec> = {};

  for (const _animDef of [...doc.querySelectorAll("Anim")]) {
    let animDef = _animDef;
    const copyOf = animDef.querySelector("CopyOf");
    const animName = animDef.querySelector("Name")!.textContent!.trim();

    // If Idle is copyOf of other, use the other. Only for Idle, to avoid fake special attack animations like Strike == Attack
    if (copyOf) {
      if (animName !== "Idle") continue;

      console.log(
        `[DEBUG] animDef: ${animDef
          .querySelector("Name")!
          .textContent!.trim()}`,
      );
      console.log(`[DEBUG] CopyOf: ${copyOf.textContent}`);

      animDef = [...doc.querySelectorAll("Anim")].find(
        (a) =>
          a.querySelector("Name")?.textContent?.trim() ===
          copyOf.textContent?.trim(),
      ) as Element;

      console.log(`[DEBUG] animDef`, animDef);
      console.log(`[DEBUG] animName`, animName);
    }

    if (!animDef) continue;

    const animSpec: AnimSpec = {
      name: copyOf
        ? animName
        : animDef.querySelector("Name")!.textContent!.trim(),
      index: Number(animDef.querySelector("Index")!.textContent),
      frameWidth: Number(animDef.querySelector("FrameWidth")!.textContent),
      frameHeight: Number(animDef.querySelector("FrameHeight")!.textContent),
      hitFrame: animDef.querySelector("HitFrame")
        ? Number(animDef.querySelector("HitFrame")!.textContent)
        : undefined,
      rushFrame: animDef.querySelector("RushFrame")
        ? Number(animDef.querySelector("RushFrame")!.textContent)
        : undefined,
      returnFrame: animDef.querySelector("ReturnFrame")
        ? Number(animDef.querySelector("ReturnFrame")!.textContent)
        : undefined,
      durations: [...animDef.querySelectorAll("Durations > Duration")].map(
        (d) => Number(d.textContent),
      ),
    };

    anims[animSpec.name] = animSpec;
  }

  return anims;
}

export type SheetSlice = {
  byDir: Record<Direction, { texture: Texture; time: number }[]>;
  cols: number;
  meta: Pick<AnimSpec, "name" | "hitFrame" | "rushFrame" | "returnFrame">;
};

// Slices an 8-row sheet using explicit frameWidth/Height.
// `durationTicksToMs`: how many ms one "tick" is in your XML (e.g., 16 for ~60fps).
export async function slice8DirSheet(
  sheet: Texture,
  frameWidth: number,
  frameHeight: number,
  durations: number[],
  durationTicksToMs = 16,
  meta: Partial<SheetSlice["meta"]> = {},
): Promise<SheetSlice> {
  const base = sheet.source;
  base.scaleMode = "nearest";
  const cols = Math.floor(base.width / frameWidth);

  // If durations shorter/longer than cols, normalize:
  const perFrameMs = (i: number) =>
    (durations[i % durations.length] ?? 0) * durationTicksToMs;

  const byDir = {} as SheetSlice["byDir"];
  for (let r = 0; r < 8; r++) {
    const row: { texture: Texture; time: number }[] = [];
    for (let c = 0; c < cols; c++) {
      row.push({
        texture: new Texture({
          source: base,
          frame: new Rectangle(
            c * frameWidth,
            r * frameHeight,
            frameWidth,
            frameHeight,
          ),
        }),
        time: perFrameMs(c),
      });
    }
    byDir[DirectionArray[r] as Direction] = row;
  }
  return {
    byDir,
    cols,
    meta: {
      name: meta.name ?? "",
      hitFrame: meta.hitFrame,
      rushFrame: meta.rushFrame,
      returnFrame: meta.returnFrame,
    },
  };
}

// Slices an 8-row idle sheet using explicit frameWidth/Height.
// Creates a sheet for the 'Rotate' animation.
// `durationTicksToMs`: how many ms one "tick" is in your XML (e.g., 16 for ~60fps).
export async function createFakeRotationSheet(
  idleSheet: Texture,
  frameWidth: number,
  frameHeight: number,
): Promise<SheetSlice> {
  const base = idleSheet.source;
  base.scaleMode = "nearest";
  const cols = Math.floor(base.width / frameWidth);

  const rotateAnimationRow = [] as SheetSlice["byDir"][Direction];

  const byDir = {} as SheetSlice["byDir"];
  for (let r = 0; r < 8; r++) {
    rotateAnimationRow.push({
      texture: new Texture({
        source: base,
        frame: new Rectangle(0, r * frameHeight, frameWidth, frameHeight),
      }),
      time: 50,
    });
  }

  byDir[Direction.N] = rotateAnimationRow;
  byDir[Direction.E] = rotateAnimationRow;
  byDir[Direction.S] = rotateAnimationRow;
  byDir[Direction.W] = rotateAnimationRow;
  byDir[Direction.NE] = rotateAnimationRow;
  byDir[Direction.NW] = rotateAnimationRow;
  byDir[Direction.SE] = rotateAnimationRow;
  byDir[Direction.SW] = rotateAnimationRow;

  return {
    byDir,
    cols,
    meta: {
      name: "",
      hitFrame: 0,
      rushFrame: 0,
      returnFrame: 0,
    },
  };
}

// Helper to build a Pixi AnimatedSprite with per-frame times already set
export function makeSprite(frames: { texture: Texture; time: number }[]) {
  // AnimatedSprite accepts frame objects with { texture, time } for per-frame duration
  const sprite = new AnimatedSprite(frames);
  sprite.anchor.set(0.5);
  sprite.loop = true;
  // animationSpeed is a global multiplier; keep 1 so "time" is exact.
  sprite.animationSpeed = 1.0;
  return sprite;
}
